import PocketBase, { ClientResponseError, type RecordModel } from 'pocketbase';

// ══════════════════════════════════════════════════════════════════════════
//  Client PocketBase — remplace `services/supabase.ts`
// ══════════════════════════════════════════════════════════════════════════
//
// PERSISTANCE DE SESSION
// ----------------------
// On garde le `LocalAuthStore` par défaut du SDK : le jeton et l'enregistrement
// utilisateur sont écrits dans `localStorage` (clé `pocketbase_auth`). C'est le
// comportement qu'avait supabase-js — la session survit au refresh et est
// partagée entre les onglets du même origin.
//
// PÉRIMÈTRE
// ---------
// Ce client ne sert QU'À L'AUTHENTIFICATION. Toutes les collections métier sont
// en deny-all côté PocketBase (cf. `pocketbase/pb_migrations/`), donc aucune
// donnée applicative ne transite par ce client : elle passe par le serveur
// Express, seul point d'autorisation (scope organisation).

const POCKETBASE_URL =
  (import.meta.env.VITE_POCKETBASE_URL as string | undefined) ?? 'http://127.0.0.1:8090';

export const pb = new PocketBase(POCKETBASE_URL);

// Par défaut le SDK annule automatiquement une requête en vol si une autre part
// sur le même couple (méthode, chemin). React 19 StrictMode monte chaque effet
// deux fois en dev : la seconde requête tuerait la première et on récupérerait
// une exception « autocancelled » à la place d'une session. On désactive donc
// l'auto-cancel — c'est l'équivalent fonctionnel du contournement de deadlock
// qu'imposait le verrou interne de supabase-js.
pb.autoCancellation(false);

/** Nom de la collection auth (ex-table `profiles` fusionnée dedans). */
export const USERS_COLLECTION = 'users';

/**
 * Vue applicative de la session, volontairement minimale.
 *
 * Le reste de l'app ne consomme que `token` (en-tête `Authorization`). Le
 * profil métier (organisation, plan, rôle…) vient de `GET /api/auth/me`, pas
 * de l'enregistrement PocketBase — c'est le serveur qui fait autorité.
 */
export interface Session {
  token: string;
  record: RecordModel | null;
}

/** Session courante lue depuis l'authStore, ou `null` si aucun jeton valide. */
export function currentSession(): Session | null {
  if (!pb.authStore.isValid || !pb.authStore.token) return null;
  return { token: pb.authStore.token, record: pb.authStore.record ?? null };
}

/**
 * MFA PocketBase : quand un second facteur est exigé, la PREMIÈRE méthode
 * d'authentification ne renvoie pas de jeton mais un **401 contenant un
 * `mfaId`**. Ce n'est donc pas une erreur « identifiants invalides » : c'est
 * l'invitation à enchaîner sur une seconde méthode en repassant ce `mfaId`.
 *
 * Renvoie le `mfaId` s'il est présent, `null` pour toute autre erreur.
 */
export function extractMfaId(err: unknown): string | null {
  if (!(err instanceof ClientResponseError)) return null;
  const mfaId = (err.response as { mfaId?: unknown } | undefined)?.mfaId;
  return typeof mfaId === 'string' && mfaId.length > 0 ? mfaId : null;
}

/**
 * Traduit une erreur SDK en message affichable.
 *
 * PocketBase renvoie ses messages en anglais et souvent très génériques
 * (« Failed to authenticate. »). On préfère systématiquement le `fallback`
 * français fourni par l'appelant, sauf pour les deux cas où le statut porte une
 * information que l'appelant ne peut pas deviner : serveur injoignable et
 * rate-limit.
 */
export function pbErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ClientResponseError) {
    if (err.status === 0) return "Serveur d'authentification injoignable. Réessayez dans un instant.";
    if (err.status === 429) return 'Trop de tentatives. Réessayez dans quelques minutes.';
    return fallback;
  }
  return fallback;
}

/**
 * Fournisseurs OAuth2 réellement activés côté PocketBase, par leur nom
 * (« google », « github »…).
 *
 * Sert à n'afficher un bouton de connexion tierce que s'il peut aboutir. Un
 * bouton présent mais non configuré échoue au clic, et l'utilisateur n'a aucun
 * moyen de comprendre pourquoi — coûteux en démonstration. Le corollaire est
 * agréable : le jour où le fournisseur est activé dans l'admin PocketBase, le
 * bouton réapparaît sans nouvelle livraison.
 *
 * Ne jette jamais : un échec réseau se lit « aucun fournisseur », ce qui
 * dégrade vers le formulaire email + mot de passe plutôt que vers un écran
 * d'erreur.
 */
export async function listOAuthProviders(): Promise<string[]> {
  try {
    const methods = await pb.collection(USERS_COLLECTION).listAuthMethods();
    return (methods.oauth2?.providers ?? []).map((p) => p.name);
  } catch {
    return [];
  }
}

/**
 * Consomme le jeton de vérification d'email envoyé à l'inscription.
 *
 * Le lien par défaut de PocketBase pointe vers sa propre interface
 * d'administration, que le proxy refuse en production : les modèles de
 * `pocketbase/mail-templates/` pointent donc vers `/verify-email?token=…`,
 * que `AuthGate` intercepte pour appeler cette fonction.
 *
 * Ne demande aucune session : le jeton porte à lui seul la preuve.
 */
export async function confirmEmailVerification(token: string): Promise<void> {
  await pb.collection(USERS_COLLECTION).confirmVerification(token);
}
